import Card from "@/components/Card";
import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import { DataNilaiRapor } from "@/types";
import { AxiosError } from "axios";
import {
  FileText,
  GraduationCap,
  Hash,
  Loader2,
  Phone,
  School,
  User,
} from "lucide-react";
import { useState, useRef } from "react";
import toast from "react-hot-toast";
import { FaArrowLeftLong, FaFaceGrinBeam } from "react-icons/fa6";
import { LuCheck, LuInfo } from "react-icons/lu";
import { MdBlock, MdClose, MdHistory, MdInfo } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type DataPendaftaran = {
  id_data_pendaftaran: string;
  nomor_pendaftaran: string;
  nisn: string;
  nama_lengkap: string;
  handphone: string;
  npsn_sekolah: string;
  nama_sekolah: string;
  pilihan1: string;
  pilihan2: string;
  path_foto: string;
};

type StateVerifikasi = {
  id_data_nilai_rapor: string;
  verifikator_ke: string | number;
  nilai_ver1?: string;
  validasi_ver1?: number;
  ket_ver1?: string;
  nilai_ver2?: string;
  validasi_ver2?: number;
  ket_ver2?: string;
  nilai_ver3?: string;
  validasi_ver3?: number;
  ket_ver3?: string;
};

const getDetailNilaiRaport = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/verifikator/peserta-rapor-smbjm/detail-nilai?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data?.data;
};

const patchDataVerifikasi = async (data: StateVerifikasi) => {
  const response = await api.patch(
    `/verifikator/peserta-rapor-smbjm/validate?verifikatorKe=${data?.verifikator_ke}`,
    data,
  );
  return response.data;
};

// Helper component for uniform Badge rendering
const RenderStatusBadge = ({ value }: { value: number }) => {
  if (value === 0)
    return (
      <Badge
        variant="secondary"
        className="dark:bg-slate-800 dark:text-slate-300 border-transparent shadow-none whitespace-nowrap"
      >
        Belum diverifikasi
      </Badge>
    );
  if (value === 1)
    return (
      <Badge
        variant="success"
        className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-transparent hover:bg-emerald-200 dark:hover:bg-emerald-900/60 shadow-none whitespace-nowrap"
      >
        Valid
      </Badge>
    );
  if (value === 2)
    return (
      <Badge variant="destructive" className="shadow-none whitespace-nowrap">
        Tidak Valid
      </Badge>
    );
  return null;
};

export default function FormVerifikasi({
  id_data_pendaftaran,
  onClose,
}: {
  id_data_pendaftaran: String | null;
  onClose: () => void;
}) {
  const [selectedNilaiRapor, setSelectedNilaiRapor] =
    useState<DataNilaiRapor | null>(null);
  const [showConfirmationValid, setShowConfirmationValid] = useState(false);
  const [showConfirmationInvalid, setShowConfirmationInvalid] = useState(false);
  const [showConfirmationReset, setShowConfirmationReset] = useState(false);

  const [selectedKeterangan, setSelectedKeterangan] = useState({
    id_data_nilai_rapor: "",
    text: "",
    nomor_verifikator: 0,
    nama_verifikator: "",
  });

  const [form, setForm] = useState({
    nilai_ver1: "",
    validasi_ver1: 0,
    ket_ver1: "",
    nilai_ver2: "",
    validasi_ver2: 0,
    ket_ver2: "",
    nilai_ver3: "",
    validasi_ver3: 0,
    ket_ver3: "",
  });

  const viewerRef = useRef<HTMLElement>(null);

  const { data, isPending } = useQuery<{
    dataPendaftaran: DataPendaftaran;
    dataNilaiRapor: DataNilaiRapor[];
    verifikatorKe: string | number;
    rataRataNilaiVer: string | number;
  }>({
    queryKey: ["detailNilaiRaport", id_data_pendaftaran],
    queryFn: () => getDetailNilaiRaport(String(id_data_pendaftaran)),
    enabled: !!id_data_pendaftaran,
  });

  const verifikatorKe = data?.verifikatorKe ?? 0;

  const keyNilai = `nilai_ver${verifikatorKe}` as keyof typeof form;
  const keyKet = `ket_ver${verifikatorKe}` as keyof typeof form;
  const keyValidasi = `validasi_ver${verifikatorKe}` as keyof typeof form;
  const keyNamaVerifikator = `nama_ver${verifikatorKe}` as keyof typeof form;

  const queryClient = useQueryClient();

  const { mutate, isPending: isPendingVerifikasi } = useMutation({
    mutationFn: patchDataVerifikasi,
    onSuccess: (resData) => {
      setSelectedNilaiRapor(resData?.data);
      queryClient.invalidateQueries({ queryKey: ["detailNilaiRaport"] });
      queryClient.invalidateQueries({ queryKey: ["listDataNilaiRaportSmbjm"] });
      toast.success(resData?.message || "Success.");

      setForm((prev) => ({
        ...prev,
        nilai_ver1: "",
        validasi_ver1: 0,
        ket_ver1: "",
        nilai_ver2: "",
        validasi_ver2: 0,
        ket_ver2: "",
        nilai_ver3: "",
        validasi_ver3: 0,
        ket_ver3: "",
      }));

      setShowConfirmationValid(false);
      setShowConfirmationInvalid(false);
      setShowConfirmationReset(false);
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleVerifikasi = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!verifikatorKe) return;

    const payload = {
      id_data_nilai_rapor: selectedNilaiRapor?.id_data_nilai_rapor || "",
      verifikator_ke: verifikatorKe,
      [`validasi_ver${verifikatorKe}`]:
        form[`validasi_ver${verifikatorKe}` as keyof typeof form],
      [`nilai_ver${verifikatorKe}`]:
        form[`nilai_ver${verifikatorKe}` as keyof typeof form],
      [`ket_ver${verifikatorKe}`]:
        form[`ket_ver${verifikatorKe}` as keyof typeof form],
    };
    mutate(payload);
  };

  const isVerifikatorPertama = verifikatorKe == 1;
  const isVerifikatorKedua = verifikatorKe == 2;
  const isVerifikatorKetiga = verifikatorKe == 3;

  const getVerifikatorLabel = () => {
    if (isVerifikatorPertama) return "Pertama";
    if (isVerifikatorKedua) return "Kedua";
    if (isVerifikatorKetiga) return "Ketiga";
    return "";
  };

  const renderInfoHover = (item: DataNilaiRapor, verLevel: number) => {
    // const isValidasiDitolak =
    //   item[`validasi_ver${verLevel}` as keyof DataNilaiRapor] == 2;
    // if (!isValidasiDitolak) return null;

    const isHovered =
      selectedKeterangan.id_data_nilai_rapor === item.id_data_nilai_rapor &&
      selectedKeterangan.nomor_verifikator === verLevel;

    return (
      <div className="relative mt-2 flex justify-center">
        <Badge
          variant="secondary"
          onMouseEnter={() =>
            setSelectedKeterangan({
              id_data_nilai_rapor: item.id_data_nilai_rapor || "",
              text:
                (item[
                  `ket_ver${verLevel}` as keyof DataNilaiRapor
                ] as string) || "",
              nomor_verifikator: verLevel,
              nama_verifikator:
                (item[
                  `nama_ver${verLevel}` as keyof DataNilaiRapor
                ] as string) || "",
            })
          }
          onMouseLeave={() => {
            setTimeout(() => {
              setSelectedKeterangan({
                id_data_nilai_rapor: "",
                text: "",
                nomor_verifikator: 0,
                nama_verifikator: "",
              });
            }, 300);
          }}
          className="cursor-help shadow-sm border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <LuInfo size={15} className="text-slate-600 dark:text-slate-300" />
        </Badge>

        {isHovered && (
          <div className="absolute z-[9999] bottom-full mb-2 -left-1/2 min-w-[200px] w-max max-w-[280px] p-3 rounded-lg shadow-xl bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 text-left border border-slate-700 dark:border-slate-300 animate-in fade-in zoom-in duration-200">
            <p className="font-semibold text-xs border-b border-slate-700 dark:border-slate-300 pb-1 mb-1">
              {selectedKeterangan.nama_verifikator}
            </p>
            <p className="text-sm italic">{selectedKeterangan.text}</p>
          </div>
        )}
      </div>
    );
  };

  if (isPending) return <SkeletonTable />;

  return (
    <div className="text-slate-900 dark:text-slate-100 transition-colors duration-200 h-full flex flex-col">
      {/* HEADER SECTION */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
        <h1 className="text-xl sm:text-2xl font-bold flex flex-wrap items-center gap-3">
          Verifikasi Berkas
          <Badge className="text-xs sm:text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500/20 dark:text-blue-300 dark:border dark:border-blue-500/30">
            Verifikator {getVerifikatorLabel()}
          </Badge>
        </h1>
        <Button
          variant="ghost"
          onClick={onClose}
          className="flex items-center w-full sm:w-auto justify-center gap-2 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400 border sm:border-transparent"
        >
          Tutup Formulir <MdClose size={18} />
        </Button>
      </div>

      {/* MAIN CONTENT AREA */}
      {/* Container ini sekarang mengalir pada mobile, dan dibatasi h-[80vh] pada Desktop */}
      <div className="flex flex-col lg:flex-row gap-6 mt-4 lg:mt-6 lg:h-[80vh]">
        {/* LEFT PANE - TABLE & STUDENT DATA */}
        <section className="w-full lg:w-[45%] flex flex-col gap-5 lg:overflow-y-auto lg:pr-2 custom-scrollbar lg:pb-10">
          {/* Table Container */}
          <div className="flex flex-col bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
            <div className="p-3 sm:p-4 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-100 dark:border-amber-900/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex items-center text-amber-800 dark:text-amber-400 text-xs sm:text-sm font-medium">
                <MdInfo className="mr-2 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                Rata-rata yg digunakan adalah Nilai Pengetahuan
              </div>
              {!isVerifikatorKetiga && (
                <span className="text-xs font-semibold bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 px-2 py-1 rounded-md">
                  Rata-rata Verifikator: {data?.rataRataNilaiVer}
                </span>
              )}
            </div>

            {/* Table wrapper untuk Scroll Horizontal di Mobile */}
            <div className="overflow-x-auto w-full">
              <table className="w-full text-sm text-left min-w-[600px] lg:min-w-full">
                <thead className="bg-slate-50 dark:bg-slate-900/80 text-slate-600 dark:text-slate-300 font-semibold border-b border-slate-200 dark:border-slate-800">
                  {isVerifikatorKetiga ? (
                    <>
                      <tr>
                        <th
                          className="px-4 py-3 border-r dark:border-slate-800"
                          rowSpan={3}
                        >
                          No
                        </th>
                        <th
                          className="px-4 py-3 border-r dark:border-slate-800"
                          rowSpan={3}
                        >
                          Berkas
                        </th>
                        <th
                          className="px-4 py-3 border-r dark:border-slate-800 text-center"
                          rowSpan={2}
                        >
                          Peserta
                        </th>
                        <th
                          className="px-4 py-2 border-r dark:border-slate-800 text-center"
                          colSpan={3}
                        >
                          Verifikator
                        </th>
                        <th className="px-4 py-3 text-center" rowSpan={3}>
                          Aksi
                        </th>
                      </tr>
                      <tr>
                        <th className="px-4 py-2 border-r border-b dark:border-slate-800 text-center">
                          Pertama
                        </th>
                        <th className="px-4 py-2 border-r border-b dark:border-slate-800 text-center">
                          Kedua
                        </th>
                        <th className="px-4 py-2 border-b dark:border-slate-800 text-center">
                          Ketiga
                        </th>
                      </tr>
                      <tr>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center bg-slate-100/50 dark:bg-slate-800/50">
                          Nilai Rata2
                        </th>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center w-[10rem] bg-slate-100/50 dark:bg-slate-800/50">
                          Nilai Rata2
                        </th>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center w-[10rem] bg-slate-100/50 dark:bg-slate-800/50">
                          Nilai Rata2
                        </th>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center w-[10rem] bg-slate-100/50 dark:bg-slate-800/50">
                          Nilai Rata2
                        </th>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr>
                        <th
                          className="px-4 py-3 border-r dark:border-slate-800"
                          rowSpan={2}
                        >
                          No
                        </th>
                        <th
                          className="px-4 py-3 border-r dark:border-slate-800"
                          rowSpan={2}
                        >
                          Berkas
                        </th>
                        <th className="px-4 py-3 border-r dark:border-slate-800 text-center">
                          Peserta
                        </th>
                        <th
                          className="px-4 py-2 border-r dark:border-slate-800 text-center"
                          colSpan={3}
                        >
                          Verifikator
                        </th>
                        <th className="px-4 py-3 text-center" rowSpan={2}>
                          Aksi
                        </th>
                      </tr>
                      <tr>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center bg-slate-100/50 dark:bg-slate-800/50 whitespace-nowrap">
                          Nilai Rata2
                        </th>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center bg-slate-100/50 dark:bg-slate-800/50 whitespace-nowrap">
                          Nilai Rata2
                        </th>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center bg-slate-100/50 dark:bg-slate-800/50 whitespace-nowrap">
                          Status
                        </th>
                        <th className="px-4 py-2 border-r dark:border-slate-800 text-center bg-slate-100/50 dark:bg-slate-800/50 whitespace-nowrap">
                          Keterangan
                        </th>
                      </tr>
                    </>
                  )}
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {data && data?.dataNilaiRapor.length > 0 ? (
                    data?.dataNilaiRapor.map(
                      (item: DataNilaiRapor, index: number) => {
                        const isActive =
                          item.id_data_nilai_rapor ===
                          selectedNilaiRapor?.id_data_nilai_rapor;
                        const hasDiscrepancy =
                          isVerifikatorKetiga &&
                          item["nilai_ver1"] !== item["nilai_ver2"];

                        return (
                          <tr
                            key={item.id_data_nilai_rapor}
                            className={`transition-colors duration-200 ${
                              isActive
                                ? "bg-blue-50/60 dark:bg-blue-900/20 hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                                : hasDiscrepancy
                                  ? "bg-red-50/30 dark:bg-red-950/20 hover:bg-red-50/50 dark:hover:bg-red-950/30"
                                  : "hover:bg-slate-50 dark:hover:bg-slate-800/40"
                            }`}
                          >
                            <td className="px-4 py-3 border-r dark:border-slate-800 text-center">
                              {index + 1}
                            </td>
                            <td className="px-4 py-3 border-r dark:border-slate-800 font-medium whitespace-nowrap">
                              Semester {item.semester}
                            </td>
                            <td className="px-4 py-3 border-r dark:border-slate-800 text-center font-semibold">
                              {item.rata_rata_nilai}
                            </td>

                            {isVerifikatorKetiga ? (
                              <>
                                <td className="px-4 py-3 border-r dark:border-slate-800 text-center align-top relative">
                                  <span className="text-base font-bold text-slate-700 dark:text-slate-200 block mb-2">
                                    {item["nilai_ver1"]}
                                  </span>
                                  <RenderStatusBadge
                                    value={item["validasi_ver1"] as number}
                                  />
                                  {renderInfoHover(item, 1)}
                                </td>
                                <td className="px-4 py-3 border-r dark:border-slate-800 text-center align-top relative">
                                  <span className="text-base font-bold text-slate-700 dark:text-slate-200 block mb-2">
                                    {item["nilai_ver2"]}
                                  </span>
                                  <RenderStatusBadge
                                    value={item["validasi_ver2"] as number}
                                  />
                                  {renderInfoHover(item, 2)}
                                </td>
                                <td className="px-4 py-3 border-r dark:border-slate-800 text-center align-top relative">
                                  <span className="text-base font-bold text-slate-700 dark:text-slate-200 block mb-2">
                                    {item["nilai_ver3"]}
                                  </span>
                                  {hasDiscrepancy &&
                                  !item["nilai_ver3"] &&
                                  !item["validasi_ver3"] ? (
                                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-transparent hover:bg-amber-200 dark:hover:bg-amber-900/60 shadow-none whitespace-nowrap">
                                      Perlu Rekon
                                    </Badge>
                                  ) : (
                                    <RenderStatusBadge
                                      value={item["validasi_ver3"] as number}
                                    />
                                  )}
                                  {/* {renderInfoHover(item, 3)} */}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3 border-r dark:border-slate-800 text-center font-bold text-slate-700 dark:text-slate-200">
                                  {item[keyNilai as keyof DataNilaiRapor]}
                                </td>
                                <td className="px-4 py-3 border-r dark:border-slate-800 text-center">
                                  <RenderStatusBadge
                                    value={
                                      item[
                                        keyValidasi as keyof DataNilaiRapor
                                      ] as number
                                    }
                                  />
                                  <p className="text-xs italic text-slate-500 dark:text-slate-400 mt-2 whitespace-nowrap">
                                    {item[keyNamaVerifikator]}
                                  </p>
                                </td>
                                <td className="px-4 py-3 border-r dark:border-slate-800 text-sm italic text-slate-600 dark:text-slate-400 min-w-[150px]">
                                  {
                                    item[
                                      `ket_ver${verifikatorKe}` as keyof DataNilaiRapor
                                    ]
                                  }
                                </td>
                              </>
                            )}

                            <td className="px-4 py-3 text-center">
                              <Button
                                variant={isActive ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  setSelectedNilaiRapor(item);
                                  // Auto scroll ke iframe pada mobile setelah render
                                  setTimeout(() => {
                                    viewerRef.current?.scrollIntoView({
                                      behavior: "smooth",
                                      block: "start",
                                    });
                                  }, 100);
                                }}
                                className={`w-full ${
                                  isActive
                                    ? "bg-blue-600 hover:bg-blue-700 text-white hover:text-white dark:bg-blue-600"
                                    : "dark:border-slate-700 dark:hover:bg-slate-800"
                                }`}
                              >
                                {isActive ? "Dilihat" : "Lihat"}
                              </Button>
                            </td>
                          </tr>
                        );
                      },
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={10}
                        className="py-8 text-center text-slate-500 dark:text-slate-400 italic"
                      >
                        Data nilai rapor tidak ditemukan...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Student Profile Card */}
          <Card className="border-slate-200 dark:border-slate-800 shadow-sm dark:bg-slate-950 overflow-auto">
            <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800/80 py-4">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <FileText className="h-5 w-5 text-blue-500" />
                Data Pendaftaran Siswa
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                {/* Photo */}
                <div className="flex-shrink-0 flex justify-center sm:justify-start">
                  <div className="w-28 sm:w-32 aspect-[3/4] h-fit overflow-hidden rounded-xl border-4 border-slate-100 dark:border-slate-800 shadow-sm relative bg-slate-100 dark:bg-slate-800">
                    {data?.dataPendaftaran?.path_foto ? (
                      <img
                        src={data?.dataPendaftaran?.path_foto}
                        alt="foto siswa"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-12 h-12 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="flex-1 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <User className="h-3.5 w-3.5" /> Nama Lengkap
                      </p>
                      <p className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
                        {data?.dataPendaftaran?.nama_lengkap}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <Hash className="h-3.5 w-3.5" /> No. Pendaftaran
                      </p>
                      <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                        {data?.dataPendaftaran?.nomor_pendaftaran}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <Hash className="h-3.5 w-3.5" /> NISN
                      </p>
                      <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                        {data?.dataPendaftaran?.nisn}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5" /> No. Handphone
                      </p>
                      <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                        {data?.dataPendaftaran?.handphone}
                      </p>
                    </div>
                  </div>

                  <Separator className="dark:bg-slate-800" />

                  <div className="space-y-4">
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-2">
                        <School className="h-3.5 w-3.5" /> Asal Sekolah
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <p className="font-semibold text-sm sm:text-base text-slate-900 dark:text-white">
                          {data?.dataPendaftaran?.nama_sekolah}
                        </p>
                        <Badge
                          variant="outline"
                          className="w-fit dark:border-slate-700 dark:text-slate-300"
                        >
                          NPSN: {data?.dataPendaftaran?.npsn_sekolah}
                        </Badge>
                      </div>
                    </div>

                    <div className="space-y-2 pt-2">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-2">
                        <GraduationCap className="h-3.5 w-3.5" /> Program Studi
                        Pilihan
                      </p>
                      <div className="flex flex-col gap-2">
                        {data?.dataPendaftaran?.pilihan1 && (
                          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 shadow-none border-transparent whitespace-nowrap">
                              Pilihan 1
                            </Badge>
                            <span className="font-medium text-xs sm:text-sm text-slate-800 dark:text-slate-200">
                              {data?.dataPendaftaran?.pilihan1}
                            </span>
                          </div>
                        )}
                        {data?.dataPendaftaran?.pilihan2 && (
                          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                            <Badge
                              variant="secondary"
                              className="shadow-none dark:bg-slate-700 dark:text-slate-300 whitespace-nowrap"
                            >
                              Pilihan 2
                            </Badge>
                            <span className="font-medium text-xs sm:text-sm text-slate-800 dark:text-slate-200">
                              {data?.dataPendaftaran?.pilihan2}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* RIGHT PANE - DOCUMENT VIEWER & ACTIONS */}
        <section
          ref={viewerRef}
          className="w-full lg:w-[55%] flex flex-col border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-900/30 overflow-hidden relative shadow-inner min-h-[600px] lg:min-h-0"
        >
          {selectedNilaiRapor ? (
            <>
              {/* Sticky Action Header */}
              <div className="z-10 sticky top-0 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 p-4 shadow-sm flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
                <div className="space-y-2 w-full xl:w-auto">
                  <div className="flex items-center justify-between sm:justify-start gap-3 w-full">
                    <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                      Berkas Rapor{" "}
                      <span className="text-blue-600 dark:text-blue-400 whitespace-nowrap">
                        Semester {selectedNilaiRapor?.semester}
                      </span>
                    </h2>
                    <RenderStatusBadge
                      value={selectedNilaiRapor?.[keyValidasi] as number}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Nilai Rata-rata Inputan:
                    <Badge
                      variant="outline"
                      className="font-bold text-sm sm:text-base bg-white dark:bg-slate-900 dark:border-slate-700"
                    >
                      {selectedNilaiRapor?.rata_rata_nilai}
                    </Badge>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
                  {selectedNilaiRapor?.[keyValidasi] !== 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700"
                      onClick={() => {
                        setShowConfirmationReset(true);
                        setForm((prev) => ({ ...prev, [keyValidasi]: 0 }));
                      }}
                    >
                      Reset <MdHistory size={16} />
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2"
                    onClick={() => {
                      setShowConfirmationInvalid(true);
                      setForm((prev) => ({ ...prev, [keyValidasi]: 2 }));
                    }}
                  >
                    Tidak Valid <MdBlock size={16} />
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white dark:bg-emerald-600/90 dark:hover:bg-emerald-600"
                    onClick={() => {
                      setShowConfirmationValid(true);
                      setForm((prev) => ({ ...prev, [keyValidasi]: 1 }));
                    }}
                  >
                    Valid <LuCheck size={18} />
                  </Button>
                </div>
              </div>

              {/* Iframe Viewer */}
              <div className="flex-1 w-full bg-slate-200 dark:bg-slate-950 p-2 sm:p-4 min-h-[400px]">
                <div className="w-full h-full rounded-lg overflow-hidden shadow-lg border border-slate-300 dark:border-slate-800 bg-white">
                  <iframe
                    src={selectedNilaiRapor?.file_rapor_semester || ""}
                    className="w-full h-full min-h-[500px] lg:min-h-full"
                    title={`Rapor Semester ${selectedNilaiRapor?.semester}`}
                  />
                </div>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="flex-1 flex flex-col justify-center items-center text-slate-400 dark:text-slate-500 p-6 sm:p-8 text-center bg-white/50 dark:bg-slate-950/50">
              <FaFaceGrinBeam size={50} className="mb-4 sm:mb-6 opacity-50" />
              <h3 className="text-lg sm:text-2xl font-bold flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-slate-500 dark:text-slate-400">
                <FaArrowLeftLong className="animate-pulse hidden lg:block" />
                Pilih berkas dari tabel untuk melihat dokumen
              </h3>
              <p className="mt-2 text-sm sm:text-base max-w-sm">
                Klik tombol "Lihat" pada tabel di atas untuk menampilkan preview
                berkas rapor di sini.
              </p>
            </div>
          )}
        </section>
      </div>

      {/* MODALS */}
      <Modal
        show={showConfirmationValid}
        onClose={() => setShowConfirmationValid(false)}
      >
        <form
          onSubmit={handleVerifikasi}
          className="bg-white dark:bg-slate-900 rounded-lg p-1 text-slate-900 dark:text-white"
        >
          <FormHeader
            title="Konfirmasi Validasi Nilai"
            description="Apakah nilai rata-rata yang diinputkan peserta sudah sesuai dengan berkas rapor?"
            icon={<></>}
          />
          <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmationValid(false)}
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto dark:border-slate-700 dark:text-slate-300"
            >
              Batal
            </Button>
            <Button
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isPendingVerifikasi ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                "Ya, Nilai Sesuai (Valid)"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showConfirmationInvalid}
        onClose={() => setShowConfirmationInvalid(false)}
      >
        <form
          onSubmit={handleVerifikasi}
          className="bg-white dark:bg-slate-900 rounded-lg p-1 text-slate-900 dark:text-white"
        >
          <FormHeader
            title={`Tolak Nilai Semester ${selectedNilaiRapor?.semester}`}
            description="Berkas ditandai tidak valid. Silakan input nilai yang benar dan berikan alasan."
            icon={<></>}
          />
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label className="dark:text-slate-300">
                Nilai Rata-Rata Seharusnya{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                required
                type="number"
                step="0.01"
                placeholder="Misal: 85.50"
                value={form[keyNilai]}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.includes(".")) {
                    const parts = val.split(".");
                    if (parts[1].length > 2) {
                      val = `${parts[0]}.${parts[1].slice(0, 2)}`;
                    }
                  }
                  setForm((prev) => ({ ...prev, [keyNilai]: val }));
                }}
                className="dark:bg-slate-950 dark:border-slate-700 dark:text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-slate-300">
                Keterangan / Alasan Penolakan{" "}
                <span className="text-red-500">*</span>
              </Label>
              <Textarea
                required
                placeholder="Contoh: Terdapat salah ketik koma pada nilai..."
                rows={3}
                value={form[keyKet]}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, [keyKet]: e.target.value }))
                }
                className="dark:bg-slate-950 dark:border-slate-700 dark:text-white"
              />
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmationInvalid(false)}
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto dark:border-slate-700 dark:text-slate-300"
            >
              Batal
            </Button>
            <Button
              variant="destructive"
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto"
            >
              {isPendingVerifikasi ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                "Tolak & Simpan Koreksi"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showConfirmationReset}
        onClose={() => setShowConfirmationReset(false)}
      >
        <form
          onSubmit={handleVerifikasi}
          className="bg-white dark:bg-slate-900 rounded-lg p-1 text-slate-900 dark:text-white"
        >
          <FormHeader
            title="Konfirmasi Reset"
            description="Apakah anda yakin ingin mengembalikan status verifikasi berkas ini menjadi 'Belum diverifikasi'?"
            icon={<></>}
          />
          <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmationReset(false)}
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto dark:border-slate-700 dark:text-slate-300"
            >
              Batal
            </Button>
            <Button
              variant="secondary"
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
            >
              {isPendingVerifikasi ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                "Ya, Reset Hasil"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
